import { Chip, Stack, TextField, Typography } from '@mui/material';
import { Incident } from '@silbo/core';
import { useTranslation } from 'react-i18next';

export function CommentStep({
  value,
  onChange,
  incident,
}: {
  value?: string;
  onChange: (value: string) => void;
  incident?: Partial<Incident>;
}) {
  const { t } = useTranslation('patient-transport');

  return (
    <Stack gap={4}>
      {incident && <Summary incident={incident} />}
      <TextField
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t('reportIncident.steps.comment.placeholder')}
        multiline
        rows={3}
      />
    </Stack>
  );
}

function Summary({ incident }: { incident: Partial<Incident> }) {
  const { t } = useTranslation('patient-transport');

  if (
    !incident.helpRequired &&
    !incident.adequateTransport &&
    !incident.delayInMinutes &&
    incident.unassignmentRequested === undefined
  ) {
    return null;
  }

  return (
    <Stack gap={2}>
      {!!incident.helpRequired && (
        <SummaryItem
          label={t('reportIncident.steps.comment.summary.helpRequired')}
          value={`${incident.helpRequired}`}
          color="secondary"
        />
      )}
      {incident.adequateTransport && (
        <SummaryItem
          label={t('reportIncident.steps.comment.summary.adequateTransport')}
          value={t(`farePositions.${incident.adequateTransport}`)}
          color="secondary"
        />
      )}
      {!!incident.delayInMinutes && (
        <SummaryItem
          label={t('reportIncident.steps.comment.summary.delay')}
          value={`${incident.delayInMinutes} min`}
          color="warning"
        />
      )}
      {incident.unassignmentRequested !== undefined && (
        <SummaryItem
          label={t('reportIncident.steps.comment.summary.blockingIncident')}
          value={
            incident.unassignmentRequested
              ? t('reportIncident.steps.comment.summary.yes')
              : t('reportIncident.steps.comment.summary.no')
          }
          color={incident.unassignmentRequested ? 'error' : 'success'}
        />
      )}
    </Stack>
  );
}

function SummaryItem({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color: 'warning' | 'error' | 'success' | 'secondary';
}) {
  return (
    <Stack direction="row" gap={3} alignItems="center">
      <Typography>{label}</Typography>
      <Chip label={value} size="x-small" color={color} variant="lightRounded" />
    </Stack>
  );
}
